import React, { useState, useEffect } from 'react';
import { Label, Input, Collapse, Card, CardBody, CardTitle, Container, Row, Col, Spinner, Tooltip } from 'reactstrap';
import { isMobile } from 'react-device-detect';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { PieChart } from 'react-minimal-pie-chart';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import Translate, { t, numStr } from '../common/Translate';
import Rotation from './Rotation';
import Block from '../common/Display/Block';
import formatRecordData from '../../services/Pricing/formatRecordData';
import useVersions from '../../config/Record/useVersions';
import useValuation from '../../config/Record/useValuation';
import mapStateToProps from '../../Store/mapping/record/mapStateToProps';
import mapDispatchToProps from '../../Store/mapping/record/mapDispatchToProps';
import { versionsListPresenter } from '../../Utils/presenters/vincheck/versionsPresenter';
import { recordActions } from '../../config/configuration/react';
import { currencySign } from '../../Utils/currencySign';

const MarketAnalysis = (props) => {
  const { recordId } = useParams();
  const dispatch = useDispatch();
  const record = useSelector((state) => state.reduxRecords.data[recordId]) || {};
  const isUkFile = record?.record?.countryId === 16;
  const veh = useSelector((state) => state.reduxRecords.data[recordId])?.vehicle;
  const { versions: versionsList, isLoading: isVersionLoading } = useVersions(recordId);
  const { valuation, updateVehicle: setVehicle, isLoading: isValuationLoading, error } = useValuation(record);
  const [form, setForm] = useState({});
  const currentSource = useSelector((state) => state.reduxRecords.data[recordId]?.sourceInfos || []);
  const [formIsValid, setFormIsValid] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState({ first: false, second: false, third: false });

  const [showAutobizPrice, setShowAutobizPrice] = useState(false);
  const versionId = _.get(veh, 'versionId', null);
  const registrationDate = _.get(veh, 'registrationDate', '');
  const mileage = _.get(veh, 'mileage', '');

  const isLoading = !(!isValuationLoading && !isVersionLoading);
  useEffect(() => {
    setForm({ versionId, registrationDate, mileage });
    setVehicle({ versionId, registrationDate, mileage });
  }, [versionId, registrationDate, mileage]);

  useEffect(() => {
    if (currentSource?.sourceGroupData?.displayAutobizPrice === true) setShowAutobizPrice(!showAutobizPrice);
    if (_.get(form, 'versionId', '') !== '' && parseInt(form.mileage, 10) > 0) {
      setFormIsValid(true);
    } else setFormIsValid(false);
  }, [form]);

  const changeForm = (e) => {
    const { id } = e.target;
    let { value } = e.target;
    if (id === 'versionId') {
      value = parseInt(value, 10);
      setForm({ ...form, versionId: value });
    } else if (id === 'mileage') {
      value = value.replace(/[^0-9]/g, '');
      if (value.length > 6) return;
      setForm({ ...form, mileage: value });
    }
  };

  const updateInfoVehicle = async () => {
    const info = [];
    info.versionId = form.versionId;
    if (versionsList) {
      versionsList.forEach((i) => {
        if (i.id === form.versionId) info.versions = i.name;
      });
    }
    info.mileage = form.mileage;
    const res = await formatRecordData(info, currentSource);
    res.recordId = recordId;
    res.automaticB2cMarketValue =
      (_.get(valuation, '_quotation.b2cMarketValue', 0) > 0 && _.get(valuation, '_quotation.b2cMarketValue')) ||
      _.get(valuation, '_quotation.corrected', null);
    Object.entries(res).forEach(([k, v]) => {
      if (v === null || v === '') _.unset(res, k);
    });
    await dispatch(recordActions.updateRemoteRecord(recordId, res));
  };

  const refresh = async () => {
    await setVehicle(form);
  };

  useEffect(() => {
    if ([1, 2, 3].includes(record?.record?.statusId)) {
      updateInfoVehicle();
    }
  }, [valuation]);

  const openInfoBull = (target) => {
    const res = { ...tooltipOpen };
    res[target] = !res[target];
    setTooltipOpen(res);
  };

  let nbSameVehicules = 0;
  let percentParticular = 0;
  let percentNetwork = 0;
  let percentOtherPro = 0;

  if (valuation && valuation._marketInformation) {
    nbSameVehicules =
      valuation._marketInformation.nbrAdsOfParticular +
      valuation._marketInformation.nbrAdsOfNetwork +
      valuation._marketInformation.nbrAdsOfOtherPro;

    percentParticular = Math.floor((valuation._marketInformation.nbrAdsOfParticular / nbSameVehicules) * 100);
    percentNetwork = Math.floor((valuation._marketInformation.nbrAdsOfNetwork / nbSameVehicules) * 100);
    percentOtherPro = Math.floor((valuation._marketInformation.nbrAdsOfOtherPro / nbSameVehicules) * 100);
  }

  const pieData = [
    { value: percentOtherPro, color: '#FF4E00' },
    { value: percentNetwork, color: '#2F397B' },
    { value: percentParticular, color: '#818099' },
  ];

  const pieOptions = {
    aspectRatio: 1,
    tooltips: {
      enabled: false,
    },
    legend: {
      display: false,
      fullWidth: false,
      labels: {
        display: false,
        usePointStyle: true,
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
    },
  };

  let alert;
  if (error) alert = <div className="text-danger text-center my-2">{t('error_getting_market_data')}</div>;
  else if (!formIsValid)
    alert = <div className="text-secondary text-center my-2">{t('complete_vehicle_info_market')}</div>;
  else if (!valuation || _.isEmpty(valuation))
    alert = <div className="text-secondary text-center my-2">{t('valid_to_get_market_info')}</div>;

  return (
    <Block
      {...props}
      backRoute={_.set(props, 'location.state.from', `/files/${recordId}`)}
      page="market_analysis_title"
      showSubHeader
    >
      <div className="page market-analysis">
        {(error && (
          <p className="ml-2 text-danger">
            <Translate code={error} />
          </p>
        )) ||
          (isLoading && (
            <div className="loader-full-screen main">
              <Spinner />
            </div>
          )) || (
            <>
              <div className="header-form">
                <div className={!isMobile && 'container-fluid'}>
                  <Row>
                    <Col className="">
                      <Row>
                        <Col xs={12} md={4} lg={4}>
                          <Label for="versionId" className="text-secondary text-uppercase font-weight-light text-left">
                            {t('version_name')}
                          </Label>
                          <Input
                            type="select"
                            className="rounded"
                            id="versionId"
                            onChange={(e) => changeForm(e)}
                            value={form.versionId || ''}
                          >
                            <option key="empty" value={null}>
                              -
                            </option>
                            {versionsList &&
                              versionsListPresenter(versionsList).map((version) =>
                                version.id === -1 ? (
                                  <option disabled>----------------------------------------------</option>
                                ) : (
                                  <React.Fragment key={version.id}>
                                    <option value={version.id}>{version.name}</option>
                                  </React.Fragment>
                                ),
                              )}
                          </Input>
                        </Col>
                        <Col xs={6} sm={5} md={3} lg={2}>
                          <Label for="mileage" className="text-secondary text-uppercase font-weight-light text-left">
                            {t(isUkFile ? 'observedMileageInMiles' : 'observed_mileage')}
                          </Label>
                          <Input
                            type="text"
                            className="form-control rounded"
                            id="mileage"
                            onChange={(e) => changeForm(e)}
                            value={(form.mileage && numStr(form.mileage)) || ''}
                          />
                        </Col>
                        <Col xs={6} sm={5} md={3} lg={2}>
                          <Label
                            for="standardMileage"
                            className="text-secondary text-uppercase font-weight-light text-left"
                          >
                            {t(isUkFile ? 'standardMileageInMiles' : 'standard_mileage')}
                          </Label>
                          <Input
                            type="text"
                            className="form-control rounded"
                            id="standardMileage"
                            value={(valuation && numStr(valuation.standardMileage)) || ''}
                            readOnly
                          />
                        </Col>
                        <Col xs={12} sm={2} md={2} lg={2} className="pb-2">
                          <Label className="d-none d-sm-block">&nbsp;</Label>
                          <button
                            type="button"
                            className="btn btn-block btn-1 px-2"
                            onClick={refresh}
                            disabled={!formIsValid}
                          >
                            <span className="d-inline-block d-sm-none d-lg-inline-block ml-2">
                              {t('analyseMarket')}
                            </span>
                          </button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              </div>

              <Container fluid className="px-0 px-md-3">
                {isLoading && (
                  <div className="loader-full-screen">
                    <Spinner />
                  </div>
                )}
                {alert || (
                  <>
                    <Row>
                      <Col className="mb-md-3">
                        <CardWindow isOpen title={t('transaction_difference')}>
                          <Row>
                            <Col xs={12} md={6}>
                              <Row className="h-100">
                                <Col xs={6} sm={4} className="my-auto">
                                  <div className="data-card corrected">
                                    <div className="data-card-label">
                                      {t('professional_value')}
                                      <InfoBullItem
                                        tooltipOpen={tooltipOpen.first}
                                        id="Tooltip-first"
                                        openInfoBull={() => openInfoBull('first')}
                                        message="professionalValueDescription"
                                      />
                                    </div>

                                    <div className="data-card-value">
                                      {currencySign(
                                        numStr(
                                          (_.get(valuation, '_quotation.b2cMarketValue', 0) > 0 &&
                                            _.get(valuation, '_quotation.b2cMarketValue')) ||
                                            _.get(valuation, '_quotation.corrected', null),
                                        ),
                                      )}
                                    </div>
                                  </div>
                                </Col>
                                <Col xs={6} sm={4} className="my-auto">
                                  <div className="data-card network">
                                    <div className="data-card-label">
                                      {t('network_value')}
                                      <InfoBullItem
                                        tooltipOpen={tooltipOpen.second}
                                        id="Tooltip-second"
                                        openInfoBull={() => openInfoBull('second')}
                                        message="networkValueDescription"
                                      />
                                    </div>
                                    <div className="data-card-value">
                                      {currencySign(numStr(_.get(valuation, '_quotation.network'), null))}
                                    </div>
                                  </div>
                                </Col>
                                <Col xs={{ size: 6, offset: 3 }} sm={{ size: 4, offset: 0 }} className="my-auto">
                                  <div className="data-card particular">
                                    <div className="data-card-label">
                                      {t('particular_value')}
                                      <InfoBullItem
                                        tooltipOpen={tooltipOpen.third}
                                        id="Tooltip-third"
                                        openInfoBull={() => openInfoBull('third')}
                                        message="particularValueDescription"
                                      />
                                    </div>
                                    <div className="data-card-value">
                                      {currencySign(numStr(_.get(valuation, '_quotation.particular', null)))}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </Col>
                            <Col xs={12} md={6}>
                              <Row>
                                <Col xs={12}>
                                  <Label className="d-none d-sm-block">&nbsp;</Label>
                                  <Table
                                    array={[
                                      {
                                        label: t('transaction_spread'),
                                        value: currencySign(
                                          numStr(_.get(valuation, '_marketInformation.transactionSpread', null)),
                                        ),
                                      },
                                    ]}
                                  />
                                </Col>
                              </Row>
                              <Row>
                                <Col xs={12} sm={{ size: 4, offset: 8 }}>
                                  <a
                                    href={valuation && _.get(valuation._links, 'marketAnalysis', '')}
                                    className="btn btn-2 btn-block px-2"
                                    onClick={(e) => !formIsValid && e.preventDefault()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span className="d-inline-block d-sm-none d-lg-inline-block ml-2">
                                      {t('fullAnalysisMarket')}
                                    </span>
                                  </a>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </CardWindow>
                      </Col>
                    </Row>

                    <Row>
                      {nbSameVehicules > 0 && (
                        <Col md={6} xl={4} className="mb-md-3">
                          <CardWindow
                            isOpen
                            title={
                              <>
                                <span className="text-corrected">{nbSameVehicules}</span> {t('same_vehicles')}
                              </>
                            }
                          >
                            {(nbSameVehicules > 0 && (
                              <Row>
                                <Col xs={6} sm={4} md={6} lg={5}>
                                  <PieChart data={pieData} options={pieOptions} startAngle={270} animate />
                                </Col>
                                <Col className="my-auto">
                                  <p className="text-secondary">
                                    <span className="text-corrected font-weight-bold">{percentOtherPro} %</span>{' '}
                                    {t('of_professionals_and_agents')}
                                  </p>
                                  <p className="text-secondary">
                                    <span className="text-network font-weight-bold">{percentNetwork} %</span>{' '}
                                    {t('of_network_sellers')}
                                  </p>
                                  <p className="text-secondary">
                                    <span className="text-particular font-weight-bold">{percentParticular} %</span>{' '}
                                    {t('of_particular')}
                                  </p>
                                </Col>
                              </Row>
                            )) || <>{t('info_indispo')}</>}
                          </CardWindow>
                        </Col>
                      )}

                      {valuation._salesSpeed.rotation > 0 && (
                        <Col md={6} xl={4} className="mb-md-3">
                          <CardWindow isOpen title={t('estimated_rotation')}>
                            <Rotation
                              salesSpeed={valuation._salesSpeed.id}
                              rotationDays={valuation._salesSpeed.rotation}
                            />
                          </CardWindow>
                        </Col>
                      )}
                    </Row>
                  </>
                )}
              </Container>
            </>
          )}
      </div>
    </Block>
  );
};

MarketAnalysis.propTypes = {
  location: PropTypes.shape().isRequired,
};

const Table = ({ array }) => (
  <table className="table table-sm ma-table">
    <tbody>
      {array.map((row, key) => (
        <tr key={row.value} className={key === 0 ? 'no-border' : ''}>
          <td className="text-secondary">{row.label}</td>
          <td className="text-right">{row.value}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

Table.propTypes = {
  array: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]).isRequired,
};

function useWindowSize() {
  const isClient = typeof window === 'object';

  function getSize() {
    return {
      width: isClient ? window.innerWidth : undefined,
    };
  }

  const [windowSize, setWindowSize] = useState(getSize);

  useEffect(() => {
    if (!isClient) {
      return false;
    }

    function handleResize() {
      setWindowSize(getSize());
    }

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowSize;
}

const CardWindow = ({ isOpen, title, children }) => {
  const size = useWindowSize();
  const [isBlocOpen, setIsBlocOpen] = useState(isOpen);
  const toggleBloc = () => setIsBlocOpen(!isBlocOpen);

  if (size.width > 753 && !isBlocOpen) setIsBlocOpen(true);

  return (
    <Card className="card-window">
      <CardTitle>
        {title}{' '}
        <FontAwesomeIcon
          className="text-corrected float-right"
          onClick={toggleBloc}
          icon={isBlocOpen ? faChevronUp : faChevronDown}
        />
      </CardTitle>
      <Collapse isOpen={isBlocOpen}>
        <CardBody>{children}</CardBody>
      </Collapse>
    </Card>
  );
};

CardWindow.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.shape().isRequired,
};

const InfoBullItem = ({ tooltipOpen, id, openInfoBull, message }) => (
  <>
    <FontAwesomeIcon icon={faInfoCircle} size="1x" id={id} className="info-icon ml-1" onClick={openInfoBull} />
    <Tooltip placement="top" isOpen={tooltipOpen} target={id} toggle={!isMobile && openInfoBull}>
      {t(message)}
    </Tooltip>
  </>
);

InfoBullItem.propTypes = {
  tooltipOpen: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  openInfoBull: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(MarketAnalysis);
